<template>
  <Teleport to="body">
    <div class="asm-backdrop" @click.self="$emit('close')">
      <div class="asm">

        <!-- Header -->
        <div class="asm-head">
          <div class="asm-av-wrap">
            <div class="asm-av" :style="{ background: avatarColor(data.name) }">
              <img v-if="data.thumbnail" :src="data.thumbnail" :alt="data.name"
                   @error="(e) => (e.target as HTMLImageElement).style.display='none'" />
              <span v-else>{{ initials(data.name) }}</span>
            </div>
            <span class="asm-avail-dot" :class="data.availability || 'offline'"></span>
          </div>
          <div class="asm-head-info">
            <div class="asm-name">{{ data.name }}</div>
            <div class="asm-role">{{ profileLabel(data.id, data.teamIds ?? []) }}</div>
            <div class="asm-email">{{ data.email }}</div>
            <span v-if="openCount > 0" class="asm-atend-badge">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
              </svg>
              em atendimento
            </span>
          </div>
          <button class="asm-close" @click="$emit('close')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Stats -->
        <div class="asm-stats">
          <div class="asm-stat">
            <div class="asm-stat-v">{{ data.conversations.length }}</div>
            <div class="asm-stat-l">Total</div>
          </div>
          <div class="asm-stat">
            <div class="asm-stat-v" style="color:var(--blue)">{{ openCount }}</div>
            <div class="asm-stat-l">Abertas</div>
          </div>
          <div class="asm-stat">
            <div class="asm-stat-v" style="color:#FF6600">{{ pendingCount }}</div>
            <div class="asm-stat-l">Pendentes</div>
          </div>
          <div class="asm-stat">
            <div class="asm-stat-v" style="color:var(--green)">{{ resolvedCount }}</div>
            <div class="asm-stat-l">Resolvidas</div>
          </div>
          <div class="asm-stat">
            <div class="asm-stat-v">{{ resolutionRate }}</div>
            <div class="asm-stat-l">Taxa Resolução</div>
          </div>
          <div class="asm-stat">
            <div class="asm-stat-v">{{ avgTime }}</div>
            <div class="asm-stat-l">Tempo Médio</div>
          </div>
        </div>

        <!-- Teams -->
        <div v-if="teamNames.length" class="asm-teams">
          <span v-for="t in teamNames" :key="t" class="asm-team-chip">{{ t }}</span>
        </div>

        <!-- Conversations -->
        <div class="asm-convs-wrap">
          <div class="asm-convs-title">
            Conversas
            <span class="asm-convs-count">{{ data.conversations.length }}</span>
          </div>
          <div v-if="!data.conversations.length" class="asm-empty">Nenhuma conversa atribuída</div>
          <div v-else class="asm-conv-list">
            <div
              v-for="c in data.conversations"
              :key="c.id"
              class="asm-conv-row"
            >
              <span class="asm-conv-id">#{{ c.id }}</span>
              <div class="asm-conv-contact">
                <div class="asm-conv-name">{{ c.contact.name }}</div>
                <div class="asm-conv-phone">{{ c.contact.phone }}</div>
              </div>
              <span class="status-pill" :class="c.status" style="font-size:10px">
                <span class="sdot"></span>{{ statusLabel(c.status) }}
              </span>
              <span class="asm-conv-team">{{ c.team?.name || '—' }}</span>
              <button class="open-chat-btn" title="Abrir chat" @click="$emit('openChat', c)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Conversation } from '@/types/dashboard'
import { avatarColor, initials, fmtDuration } from '@/composables/useHelpers'
import { profileLabel } from '@/composables/useAdmin'

interface AgentData {
  id: number
  name: string
  email: string
  role: string
  teamIds?: number[]
  thumbnail: string
  availability: string
  conversations: Conversation[]
}

const props = defineProps<{ data: AgentData }>()
defineEmits<{ close: []; openChat: [conv: Conversation] }>()

const openCount    = computed(() => props.data.conversations.filter(c => c.status === 'open').length)
const pendingCount = computed(() => props.data.conversations.filter(c => c.status === 'pending').length)
const resolvedCount = computed(() => props.data.conversations.filter(c => c.status === 'resolved').length)

const resolutionRate = computed(() => {
  const total = props.data.conversations.length
  if (!total) return '—'
  return Math.round((resolvedCount.value / total) * 100) + '%'
})

const avgTime = computed(() => {
  const resolved = props.data.conversations.filter(c => c.status === 'resolved' && c.resolved_at > 0)
  if (!resolved.length) return '—'
  const avg = resolved.reduce((s, c) => s + (c.resolved_at - c.created_at), 0) / resolved.length
  return fmtDuration(avg)
})

const teamNames = computed(() => {
  const s = new Set(props.data.conversations.map(c => c.team?.name).filter(Boolean) as string[])
  return [...s]
})

function statusLabel(s: string) {
  const map: Record<string, string> = { open: 'Aberta', resolved: 'Resolvida', pending: 'Pendente', snoozed: 'Adiada' }
  return map[s] || s
}
</script>
